import { useCallback, useEffect, useRef, useState } from 'react';
import './SplitDivider.css';

// A line between two sides of a screen, which the person at it can move
// (Jimmy, 2026-10-15 and 10-19): the brief and the whiteboard in the meeting,
// the preparation panel and the brief's preview on the brief screen. Drag it
// to give either side more room; drag it nearly to an edge, or use the arrows
// on it, and that side goes away, leaving a tab at the edge that brings it
// back at the width it had. Double-click the line, or use the middle button,
// for an even split.
//
// How a screen is divided is a convenience for whoever is at this computer,
// so it is kept in this browser only and never on the assessment record.

// The narrowest a side may be while both are shown, as a share of the width.
const MIN = 20;
const MAX = 100 - MIN;
// Let go within this distance of an edge and that side is put away.
const COLLAPSE = 10;
// Within this distance of the middle a drag settles on an even split.
const SNAP = 2;
const STEP = 5;

/** The meeting's two sides, the default. */
export const MEETING_SIDES = [
  { id: 'brief', name: 'the brief', tab: 'Brief' },
  { id: 'board', name: 'the whiteboard', tab: 'Whiteboard' },
];

function readSaved(key, initial, ids) {
  try {
    const s = JSON.parse(window.localStorage.getItem(key) || 'null');
    if (s && typeof s.pct === 'number' && s.pct >= MIN && s.pct <= MAX) {
      return { pct: s.pct, hidden: ids.includes(s.hidden) ? s.hidden : null };
    }
  } catch { /* a blocked or empty store starts from the default */ }
  return { pct: initial, hidden: null };
}

function clamp(pct) {
  if (Math.abs(pct - 50) <= SNAP) return 50;
  return Math.min(MAX, Math.max(MIN, pct));
}

/**
 * The first side's share of the width and which side, if either, is put
 * away. `pct` is kept while a side is hidden, so bringing it back restores it.
 * `key` is where this browser keeps it; `initial` is the first side's share
 * before anyone has moved the line.
 */
export function useSplit({ key = 'pq.meeting.split', initial = 46, sides = MEETING_SIDES } = {}) {
  const ids = sides.map(s => s.id);
  const [split, setSplit] = useState(() => readSaved(key, initial, ids));
  useEffect(() => {
    try { window.localStorage.setItem(key, JSON.stringify(split)); } catch { /* kept for this visit only */ }
  }, [key, split]);
  const setPct = useCallback(pct => setSplit({ pct: clamp(pct), hidden: null }), []);
  const hide = useCallback(side => setSplit(s => ({ ...s, hidden: side })), []);
  const show = useCallback(() => setSplit(s => ({ ...s, hidden: null })), []);
  const even = useCallback(() => setSplit({ pct: 50, hidden: null }), []);
  return { ...split, setPct, hide, show, even, sides };
}

function Chevron({ dir }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d={dir === 'left' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
    </svg>
  );
}

function EvenIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M12 5v14" />
    </svg>
  );
}

/**
 * The divider itself. `containerRef` is the row it divides, measured to turn
 * the pointer's position into a share of the width. While a side is hidden it
 * becomes the tab that brings that side back.
 */
export default function SplitDivider({ split, containerRef }) {
  const { pct, hidden, setPct, hide, show, even, sides = MEETING_SIDES } = split;
  const [first, second] = sides;
  const [drag, setDrag] = useState(null);
  const dragging = useRef(false);

  const shareAt = useCallback(clientX => {
    const r = containerRef.current?.getBoundingClientRect();
    if (!r || !r.width) return pct;
    return ((clientX - r.left) / r.width) * 100;
  }, [containerRef, pct]);

  // Moving the line. The row shows where it will land as it moves, and
  // letting go near an edge puts that side away.
  const onPointerDown = e => {
    if (e.button !== undefined && e.button !== 0) return;
    if (e.target.closest('button')) return;
    e.preventDefault();
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag(shareAt(e.clientX));
  };
  const onPointerMove = e => {
    if (!dragging.current) return;
    setDrag(shareAt(e.clientX));
  };
  const onPointerUp = e => {
    if (!dragging.current) return;
    dragging.current = false;
    const at = shareAt(e.clientX);
    setDrag(null);
    if (at < COLLAPSE) hide(first.id);
    else if (at > 100 - COLLAPSE) hide(second.id);
    else setPct(at);
  };

  const onKeyDown = e => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); if (pct - STEP < MIN) hide(first.id); else setPct(pct - STEP); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); if (pct + STEP > MAX) hide(second.id); else setPct(pct + STEP); }
    else if (e.key === 'Home') { e.preventDefault(); hide(first.id); }
    else if (e.key === 'End') { e.preventDefault(); hide(second.id); }
    else if (e.key === 'Enter' || e.key === '=') { e.preventDefault(); even(); }
  };

  // The live position while dragging, for the row to follow.
  useEffect(() => {
    const row = containerRef.current;
    if (!row) return undefined;
    if (drag === null) {
      row.style.removeProperty('--split-live');
      row.classList.remove('is-resizing');
      return undefined;
    }
    row.classList.add('is-resizing');
    row.style.setProperty('--split-live', `${Math.min(100, Math.max(0, drag))}%`);
    return undefined;
  }, [drag, containerRef]);

  if (hidden) {
    const isFirst = hidden === first.id;
    const side = isFirst ? first : second;
    return (
      <button
        type="button"
        className={`conv-split-tab no-print is-${isFirst ? 'left' : 'right'}`}
        onClick={show}
        aria-label={`Show ${side.name}`}
        title={`Show ${side.name}`}
      >
        <Chevron dir={isFirst ? 'right' : 'left'} />
        <span>{side.tab}</span>
      </button>
    );
  }

  const near = drag === null ? null : drag < COLLAPSE ? first.id : drag > 100 - COLLAPSE ? second.id : null;
  return (
    <div
      className={`conv-split-divider no-print${drag !== null ? ' is-dragging' : ''}${near ? ` will-hide-${near}` : ''}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${first.name} and ${second.name}`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      aria-valuetext={`${first.tab} ${Math.round(pct)}%, ${second.tab.toLowerCase()} ${Math.round(100 - pct)}%`}
      tabIndex={0}
      title="Drag to resize. Double-click for an even split."
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => { dragging.current = false; setDrag(null); }}
      onDoubleClick={e => { if (!e.target.closest('button')) even(); }}
      onKeyDown={onKeyDown}
    >
      <div className="conv-split-handle">
        <button type="button" onClick={() => hide(first.id)} aria-label={`Hide ${first.name}`} title={`Hide ${first.name}`}>
          <Chevron dir="left" />
        </button>
        <button type="button" onClick={even} aria-label="Split evenly" title="Split evenly" aria-pressed={pct === 50}>
          <EvenIcon />
        </button>
        <button type="button" onClick={() => hide(second.id)} aria-label={`Hide ${second.name}`} title={`Hide ${second.name}`}>
          <Chevron dir="right" />
        </button>
      </div>
    </div>
  );
}
